import { BillingAccountViewV1Schema } from "@fulcrum5/suite-contracts";
import { Check, RotateCw } from "lucide-react";
import { useCallback } from "react";
import { useOrganizationResource } from "./useOrganizationResource.ts";
import "./dashboardPages.css";

const BILLING_PATH = (organizationId: string) => `/api/organizations/${organizationId}/billing`;

export function BillingPage(): JSX.Element {
  const resource = useOrganizationResource({
    path: BILLING_PATH,
    responseSchema: BillingAccountViewV1Schema,
  });
  const retry = useCallback(() => { void resource.reload(); }, [resource]);

  if (resource.status === "loading" && !resource.data) {
    return <section className="platform-page dashboard-page"><p className="platform-loading" role="status">Loading billing.</p></section>;
  }
  if (!resource.data && resource.status === "idle") {
    return <section className="platform-page dashboard-page"><p className="platform-empty-state" role="status">An active organization is required to view billing.</p></section>;
  }
  if (!resource.data) {
    return (
      <section className="platform-page dashboard-page">
        <div className="platform-notice platform-notice--error" role="alert">
          <p>{resource.message ?? "Billing could not be loaded."}</p>
          {resource.canRetry ? <button className="platform-button platform-button--secondary" onClick={retry} type="button">Retry billing</button> : null}
        </div>
      </section>
    );
  }

  const account = resource.data;
  return (
    <section className="platform-page dashboard-page billing-page" aria-labelledby="billing-page-title">
      <header className="dashboard-page__header">
        <div>
          <p className="platform-page__eyebrow">Workspace</p>
          <h1 id="billing-page-title">Billing</h1>
        </div>
        <span className="platform-badge" data-tone={account.accessStatus}>{account.accessLabel}</span>
      </header>

      {resource.status === "error" ? (
        <div className="platform-notice platform-notice--error dashboard-page__notice" role="alert">
          <p>{resource.message ?? "Billing could not be refreshed."}</p>
          {resource.canRetry ? <button className="platform-button platform-button--secondary" onClick={retry} type="button">Retry billing</button> : null}
        </div>
      ) : null}

      <section className="billing-page__plan" aria-label="Current plan">
        <div>
          <p className="dashboard-kicker">Current plan</p>
          <h2>{account.planName}</h2>
        </div>
        <p className="billing-page__price">{account.priceLabel}</p>
      </section>

      <section className="billing-page__included" aria-labelledby="billing-included-title">
        <div className="dashboard-section-heading">
          <div>
            <p className="dashboard-kicker">Access</p>
            <h2 id="billing-included-title">Included products</h2>
          </div>
        </div>
        <ul aria-label="Included products">
          {account.includedProducts.map((product) => (
            <li key={product}><Check aria-hidden="true" size={17} strokeWidth={2} />{product}</li>
          ))}
        </ul>
      </section>

      <section className="billing-page__payment" aria-label="Payment method">
        <p className="dashboard-kicker">Payment method</p>
        <p>Payment method not required</p>
      </section>
      {resource.status === "loading" ? <p className="dashboard-page__refreshing" role="status"><RotateCw aria-hidden="true" className="platform-spin" size={15} /> Refreshing billing.</p> : null}
    </section>
  );
}
